import React, {useRef, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {Box, Flex, Text, Icon, Link as ChakraLink, useBreakpointValue, useStyleConfig} from '@chakra-ui/react'
import {useLocation} from 'react-router-dom'
import {ChevronDownIcon, Globe, GlobeDark} from '../icons'
import {GarageLogo, DynamiteLogo} from '../icons/inline-brand-logos'
import CountrySelector from '../country-selector'
import {useGlobalTopBanner} from '../../contexts/global-elements-context'
import GDILinkBlock from '../optimizely/gdi/assets/gdiLinkBlock'
import {buildViewTypeStyles} from '../optimizely/utils/utils'
import {useBrand, getBrandSwitchUrl} from '../../contexts/brand-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useSessionSharing} from '../../hooks/use-session-sharing'

const TopBar = ({showCountrySelectorOnFooter, variant}) => {
    const intl = useIntl()
    const location = useLocation()

    const isMobile = useBreakpointValue({base: true, md: false})
    const isDesktop = useBreakpointValue({base: false, md: true})
    const countrySelectorRef = useRef(null)
    const {brandId} = useBrand()
    const {site} = useMultiSite()
    const siteId = site?.id
    const showCountrySelectorInHeader = showCountrySelectorOnFooter === false
    // Computed post-mount only: the URLs depend on window.location (host/protocol),
    // which is absent during SSR. Computing during render would make the client's
    // first render differ from the server (href null -> url) and break hydration.
    // Deferring to an effect keeps the first client render matching the server.
    const [brandSwitchUrls, setBrandSwitchUrls] = useState({})
    useEffect(() => {
        const {host, protocol} = window.location
        const {pathname, search, hash} = location
        setBrandSwitchUrls({
            garage: getBrandSwitchUrl(host, 'garage', {pathname, search, hash, protocol, siteId}),
            dynamite: getBrandSwitchUrl(host, 'dynamite', {pathname, search, hash, protocol, siteId})
        })
    }, [location.pathname, location.search, location.hash, siteId])
    // Get top banner content from global elements context
    const {data: topBanner} = useGlobalTopBanner()
    const topBannerText = topBanner?.texts?.[0]
    const textForWeb = topBannerText?.textForWeb || ''
    const textStyles = topBannerText?.stylesPerViewType || []

    // Variation: CMS value ("Light"/"Dark") takes priority, fallback to variant prop, default 'dark'
    const cmsVariant = topBanner?.variation?.toLowerCase()
    const resolvedVariant = (cmsVariant === 'light' || cmsVariant === 'dark') ? cmsVariant : (variant || 'dark')

    const styles = useStyleConfig('TopBar', {variant: resolvedVariant})

    const sisterBrandId = brandId === 'dynamite' ? 'garage' : 'dynamite'
    const GlobeIcon = resolvedVariant === 'light' ? GlobeDark : Globe
    const {getRedirectURL} = useSessionSharing()

    const handleBrandClick = async (event, targetBrandId, isActive) => {
        event.preventDefault()
        if (isActive) return
        if (typeof window === 'undefined') return
        const {host, protocol} = window.location
        const {pathname, search, hash} = location
        const targetUrl =
            getBrandSwitchUrl(host, targetBrandId, {pathname, search, hash, protocol, siteId})
        if (targetUrl) {
            let nextDomain = getBrandSwitchUrl(host, targetBrandId, {siteId})
            const localeAliases = (site?.l10n?.supportedLocales || []).flatMap((l) =>
                [l.alias, l.id].filter(Boolean)
            )
            const firstSegment = pathname.split('/').filter(Boolean)[0]

            if (firstSegment && localeAliases.includes(firstSegment)) {
                nextDomain = `${nextDomain.replace(/\/$/, '')}/${firstSegment}`
            }

            const redirectURL = await getRedirectURL(nextDomain)
            window.location.href = redirectURL || targetUrl
        }
    }

    return (
        <Box {...styles.container}>
            <Flex {...styles.inner}>
                <Flex {...styles.brandToggle}>
                    {[brandId, sisterBrandId].map((brandOption, index) => {
                        const isActive = brandOption === brandId
                        const href = brandSwitchUrls[brandOption]
                        const brandName = brandOption.charAt(0).toUpperCase() + brandOption.slice(1)
                        return (
                            <React.Fragment key={brandOption}>
                                <ChakraLink
                                    href={href}
                                    data-testid={`header-brand-switch-${brandOption}`}
                                    {...styles.brandButton}
                                    {...(isActive
                                        ? styles.brandButtonActive
                                        : styles.brandButtonInactive)}
                                    aria-current={isActive ? 'page' : undefined}
                                    aria-label={intl.formatMessage(
                                        {
                                            id: 'header.brand.switch.label',
                                            defaultMessage: '{brandName} - Go to homepage'
                                        },
                                        {brandName}
                                    )}
                                    onClick={(event) =>
                                        handleBrandClick(event, brandOption, isActive)
                                    }
                                >
                                    {brandOption === 'garage' ? (
                                        <GarageLogo />
                                    ) : (
                                        <DynamiteLogo />
                                    )}
                                </ChakraLink>
                            </React.Fragment>
                        )
                    })}
                </Flex>
                {/* as="div": CMS textForWeb can contain block-level HTML (e.g. <h4>).
                    A <p> (Text default) auto-closes around block children when the SSR
                    HTML string is parsed, ejecting them out of this styled wrapper so
                    they lose webH4 (uppercase/bold) — client render keeps them nested,
                    causing an SSR/client mismatch. A <div> nests block content validly. */}
                <Text as="div" {...styles.message} sx={buildViewTypeStyles(textStyles)}>
                    {topBanner?.link ? (
                        <GDILinkBlock {...topBanner.link}>
                            <Box as="span" dangerouslySetInnerHTML={{__html: textForWeb}} />
                        </GDILinkBlock>
                    ) : textForWeb ? (
                        <Box as="span" dangerouslySetInnerHTML={{__html: textForWeb}} />
                    ) : null}
                </Text>

                {isMobile && showCountrySelectorInHeader && (
                    <Flex
                        data-testid="header-country-selector-mobile"
                        {...styles.mobileTrigger}
                        onClick={() => {
                            if (isMobile && countrySelectorRef.current?.onToggle) {
                                countrySelectorRef.current.onToggle()
                            }
                        }}
                        onKeyDown={(e) => {
                            e.preventDefault()
                            if (isMobile && e.key === 'Enter' && countrySelectorRef.current?.onToggle) {
                                countrySelectorRef.current.onToggle()
                            }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                            id: 'header.country.selector.label',
                            defaultMessage: 'Country selector'
                        })}
                    >
                        <Icon as={GlobeIcon} {...styles.globeIcon} />
                        <CountrySelector triggerRef={countrySelectorRef} variant={resolvedVariant} />
                        <Icon as={ChevronDownIcon} {...styles.chevronIcon} />
                    </Flex>
                )}

                {isDesktop && showCountrySelectorInHeader && (
                    <Flex data-testid="header-country-selector-desktop" {...styles.desktopTrigger}>
                        <CountrySelector triggerRef={countrySelectorRef} variant={resolvedVariant} />
                    </Flex>
                )}
            </Flex>
        </Box>
    )
}

export default TopBar
